import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserStatsAPI, apiService } from '../../api/api.service';
import { useNotification } from '../shared/NotificationSystem';
import EditIcon from '../icons/EditIcon';
import CloseIcon from '../icons/CloseIcon';
import styles from './UserModal.module.scss';

interface UserModalProps {
    user: UserStatsAPI | null;
    isOpen: boolean;
    onClose: () => void;
    onUserUpdate: (updatedUser?: UserStatsAPI) => void;
    getLocalizedStatus: (status: string | null) => string;
}

const UserModal: React.FC<UserModalProps> = ({
    user,
    isOpen,
    onClose,
    onUserUpdate,
    getLocalizedStatus
}) => {
    const { t } = useTranslation();
    const { showSuccess, showError } = useNotification();
    const [isEditingExpiration, setIsEditingExpiration] = useState(false);
    const [newExpirationDate, setNewExpirationDate] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [localUser, setLocalUser] = useState<UserStatsAPI | null>(user);

    // Синхронизируем локальное состояние с пропом user
    useEffect(() => {
        setLocalUser(user);
    }, [user]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    if (!isOpen || !localUser) return null;

    const handleEditExpiration = () => {
        setIsEditingExpiration(true);
        // Устанавливаем текущую дату окончания подписки или дату регистрации, если дата окончания меньше
        if (!localUser.subscription_end_date || !localUser.registration_date) {
            setNewExpirationDate('');
            return;
        }
        const subscriptionEndDate = new Date(localUser.subscription_end_date.split('T')[0]);
        const registrationDate = new Date(localUser.registration_date.split('T')[0]);
        const dateToUse = subscriptionEndDate >= registrationDate ? localUser.subscription_end_date : localUser.registration_date;
        setNewExpirationDate(dateToUse.split('T')[0]);
    };

    const handleSaveExpiration = async () => {
        if (!newExpirationDate || !localUser) return;

        setIsLoading(true);
        try {
            // Создаем абсолютную дату без учета часовых поясов
            // Формируем ISO строку напрямую, чтобы избежать проблем с часовыми поясами
            const absoluteDateTime = `${newExpirationDate}T23:59:59.999Z`;

            await apiService.updateSubscriptionExpiration(localUser.id, localUser.language, absoluteDateTime);
            showSuccess('statistics.subscriptionUpdated');

            // Обновляем локальное состояние пользователя
            const updatedUser = { ...localUser, subscription_end_date: absoluteDateTime };
            setLocalUser(updatedUser);

            onUserUpdate(updatedUser);
            setIsEditingExpiration(false);
        } catch (error) {
            showError('statistics.subscriptionUpdateError');
            console.error('Update subscription error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingExpiration(false);
        setNewExpirationDate('');
    };

    // Функция для абсолютного форматирования даты (без учета часовых поясов)
    const formatAbsoluteDate = (dateString: string | null) => {
        if (!dateString) return '-';
        // Извлекаем дату напрямую из ISO строки, игнорируя часовой пояс
        const datePart = dateString.split('T')[0]; // Получаем "2025-09-15"
        const [year, month, day] = datePart.split('-');
        return `${day}.${month}.${year}`;
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{t('statistics.userInfo')}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        <CloseIcon />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.userInfoGrid}>
                        <div className={styles.infoItem}>
                            <label>{t('statistics.userName')}:</label>
                            <span>{localUser.name}</span>
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.email')}:</label>
                            <span>{localUser.email || '-'}</span>
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.subscriptionStatus')}:</label>
                            <span className={`${styles.statusBadge} ${styles[`status-${localUser.subscription_status?.toLowerCase() || 'unknown'}`]}`}>
                                {getLocalizedStatus(localUser.subscription_status)}
                            </span>
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.language')}:</label>
                            <span>{localUser.language}</span>
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.registrationDate')}:</label>
                            <span>{formatAbsoluteDate(localUser.registration_date)}</span>
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.lastLogin')}:</label>
                            <span>
                                {(() => {
                                    if (!localUser.last_login_date || !localUser.registration_date) return '-';
                                    const lastLoginDate = new Date(localUser.last_login_date.split('T')[0]);
                                    const registrationDate = new Date(localUser.registration_date.split('T')[0]);
                                    const shouldShowLastLogin = lastLoginDate >= registrationDate;
                                    return shouldShowLastLogin ? formatAbsoluteDate(localUser.last_login_date) : '-';
                                })()}
                            </span>
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.completedTrainingsLast30Days')}:</label>
                            <span>{localUser.completed_trainings_last_30_days}</span>
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.totalTrainings')}:</label>
                            <span>{localUser.completed_trainings_count}</span>
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.subscriptionEndDate')}:</label>
                            <div className={styles.expirationContainer}>
                                {isEditingExpiration ? (
                                    <div className={styles.editContainer}>
                                        <input
                                            type="date"
                                            value={newExpirationDate}
                                            onChange={(e) => setNewExpirationDate(e.target.value)}
                                            placeholder={t('statistics.datePlaceholder')}
                                            title={t('statistics.datePlaceholder')}
                                            lang={localUser.language}
                                            className={styles.dateInput}
                                        />
                                        <div className={styles.editButtons}>
                                            <button
                                                onClick={handleSaveExpiration}
                                                disabled={isLoading}
                                                className={styles.saveButton}
                                            >
                                                {isLoading ? t('common.loading') : t('common.save')}
                                            </button>
                                            <button
                                                onClick={handleCancelEdit}
                                                className={styles.cancelButton}
                                            >
                                                {t('common.cancel')}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.expirationDisplay}>
                                        <span>
                                            {(() => {
                                                if (!localUser.subscription_end_date || !localUser.registration_date) return '-';
                                                const subscriptionEndDate = new Date(localUser.subscription_end_date.split('T')[0]);
                                                const registrationDate = new Date(localUser.registration_date.split('T')[0]);
                                                return subscriptionEndDate >= registrationDate
                                                    ? formatAbsoluteDate(localUser.subscription_end_date)
                                                    : '-';
                                            })()}
                                        </span>
                                        <button
                                            onClick={handleEditExpiration}
                                            className={styles.editButton}
                                            title={t('statistics.editSubscriptionExpiration')}
                                        >
                                            <EditIcon />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserModal;
